/**
 * ==========================================================================
 *  THE BOTTLE
 * ==========================================================================
 *  A photograph of a Karhu III, keyed onto a single plane parented to the
 *  camera. Kimmo carries it low on the right, and every time E goes into the
 *  bottle instead of into a door it comes up to his lip and tips.
 *
 *  Purely cosmetic. `sip()` is called from the render side of Game after the
 *  simulation has already decided a sip happened, and nothing here is ever
 *  read back. See src/config/viewmodel.ts for the poses and the units.
 *
 *  ------------------------------------------------------------------------
 *   THREE THINGS THAT ARE NOT OBVIOUS
 *  ------------------------------------------------------------------------
 *  1. The plane hangs *below* an empty group and is rotated by rotating the
 *     group. The group sits where the bottle's mouth is, so a roll is a bottle
 *     tipping at your lip rather than a picture spinning around its middle.
 *
 *  2. Depth testing is off and the render order is last. A viewmodel half a
 *     metre from the eye is inside the wall the moment you stand against one,
 *     and the cheap fix - draw it flat, on top, after everything - is also the
 *     one that costs nothing on a laptop iGPU.
 *
 *  3. The camera has to be added to the scene by the caller. three.js only
 *     walks the graph it is given, and a camera that is not in it never has
 *     its children drawn.
 *
 *  This is the one texture in the game that is a file rather than a canvas
 *  drawn at load time (see src/render/surfaces.ts for why the rest are not).
 *  The joke is that it is a real photograph of a real bottle; a procedural
 *  approximation of one would not be the same joke.
 * ==========================================================================
 */

import * as THREE from 'three';
import { VIEWMODEL } from '../config/viewmodel';
import { BeerSwing, type SwingInput } from './BeerSwing';
import bottleUrl from './textures/kalja-pullo.png';

export interface ViewmodelFrame {
  /** Render seconds since the last frame. */
  dt: number;
  visible: boolean;
  yaw: number;
  pitch: number;
  /** Horizontal ground speed, m/s. */
  speed: number;
  /** The camera's head bob this frame, in metres. */
  bobLateral: number;
  bobVertical: number;
  swayIntensity: number;
  /** The lurch the sip in flight is applying, local space, x = right. */
  lurchX: number;
  lurchY: number;
}

export class BeerViewmodel {
  private readonly swing = new BeerSwing();
  /** Sits at the bottle's mouth. Rotating this tips the bottle at the lip. */
  private readonly pivot = new THREE.Group();
  private readonly geometry = new THREE.PlaneGeometry(1, 1);
  private readonly material: THREE.MeshBasicMaterial;
  private readonly mesh: THREE.Mesh;
  private texture: THREE.Texture | null = null;
  /** Width over height of the keyed image, once it has arrived. */
  private imageAspect = 1;
  private dropped = false;
  private dropAge = 0;
  private readonly dropStart = new THREE.Vector3();
  private readonly dropDrift = new THREE.Vector3();
  private readonly dropRotation = new THREE.Quaternion();
  private readonly tumble = new THREE.Quaternion();
  private readonly tumbleAxis = new THREE.Vector3(0, 0, 1);
  private floorY = 0;

  constructor(
    private readonly camera: THREE.PerspectiveCamera,
    maxAnisotropy = 1,
  ) {
    this.material = new THREE.MeshBasicMaterial({
      transparent: true,
      // See note 2 in the header: never let the world clip the bottle.
      depthTest: false,
      depthWrite: false,
      // Half a metre from the eye is well inside `RENDER.fogNear`, but fog on a
      // hand-held object is wrong at any distance.
      fog: false,
    });

    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.renderOrder = 1000;
    this.mesh.frustumCulled = false;
    this.pivot.add(this.mesh);
    // Hidden until the image is decoded, or the first frames draw a white slab.
    this.pivot.visible = false;
    camera.add(this.pivot);

    new THREE.TextureLoader().load(
      bottleUrl,
      (texture) => this.onTextureLoaded(texture, maxAnisotropy),
      undefined,
      () => console.warn('[laniaamu] kalja-pullo.png ei latautunut - pullo jää piiloon'),
    );
  }

  /** E went into the bottle. Starts the pull; see BeerSwing for the timing. */
  sip(): void {
    if (!this.dropped) this.swing.sip();
  }

  /** Release the photographed bottle into the world, preserving its last hand pose. */
  drop(scene: THREE.Scene, floorY = 0): void {
    if (this.dropped) return;
    this.dropped = true;
    this.dropAge = 0;
    this.floorY = floorY;
    this.pivot.updateWorldMatrix(true, true);
    this.mesh.getWorldPosition(this.dropStart);
    scene.attach(this.pivot);
    // Tumble around the bottle's centre once it is no longer held by the neck.
    this.pivot.position.copy(this.dropStart);
    this.mesh.position.y = 0;
    this.dropRotation.copy(this.pivot.quaternion);
    this.dropDrift.set(0.12, 0, -0.2).applyQuaternion(this.camera.quaternion);
    this.dropDrift.y = 0;
    this.material.depthTest = true;
    this.material.opacity = 1;
  }

  reset(): void {
    this.dropped = false;
    this.dropAge = 0;
    this.camera.add(this.pivot);
    this.material.depthTest = false;
    this.swing.reset();
    this.pivot.visible = false;
  }

  /** Returns true exactly once, on the frame the released bottle hits the floor. */
  update(frame: ViewmodelFrame): boolean {
    if (this.dropped) {
      const duration = VIEWMODEL.dropDuration;
      const previous = this.dropAge;
      const dt = frame.visible ? Math.max(0, frame.dt) : 0;
      this.dropAge = Math.min(duration, this.dropAge + dt);
      const t = this.dropAge / duration;
      this.pivot.visible = frame.visible && this.texture !== null && t < 1;
      this.pivot.position.copy(this.dropStart).addScaledVector(this.dropDrift, t);
      this.pivot.position.y = THREE.MathUtils.lerp(this.dropStart.y, this.floorY + 0.04, t * t);
      this.tumble.setFromAxisAngle(this.tumbleAxis, -t * 1.8);
      this.pivot.quaternion.copy(this.dropRotation).multiply(this.tumble);
      return previous < duration && this.dropAge >= duration;
    }
    // Taken from the camera every frame rather than cached: the FOV is a slider
    // the player can drag mid-run, and the aspect changes when the window does.
    const halfHeight = Math.tan(THREE.MathUtils.degToRad(this.camera.fov) / 2) * VIEWMODEL.depth;
    const halfWidth = halfHeight * this.camera.aspect;

    const input: SwingInput = {
      dt: frame.dt,
      visible: frame.visible,
      yaw: frame.yaw,
      pitch: frame.pitch,
      speed: frame.speed,
      // The bob arrives in metres and the swing thinks in screen units, so the
      // conversion happens here, where the metres-per-screen is known.
      bobLateral: frame.bobLateral / halfHeight,
      bobVertical: frame.bobVertical / halfHeight,
      swayIntensity: frame.swayIntensity,
      lurchX: frame.lurchX,
      lurchY: frame.lurchY,
    };
    const pose = this.swing.update(input);

    this.pivot.visible = this.texture !== null && pose.opacity > 0.002;
    if (!this.pivot.visible) return false;

    this.pivot.position.set(pose.x * halfWidth, pose.y * halfHeight, -VIEWMODEL.depth);
    // XYZ order applies the roll first, so the bottle tips in the plane of the
    // screen and *then* the whole thing leans away from the eye.
    this.pivot.rotation.set(pose.pitch, 0, pose.roll);

    const height = pose.height * 2 * halfHeight;
    this.mesh.scale.set(height * this.imageAspect, height, 1);
    this.mesh.position.y = -height / 2;
    this.material.opacity = pose.opacity;
    return false;
  }

  dispose(): void {
    this.pivot.removeFromParent();
    this.geometry.dispose();
    this.material.dispose();
    this.texture?.dispose();
  }

  private onTextureLoaded(texture: THREE.Texture, maxAnisotropy: number): void {
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = Math.min(VIEWMODEL.anisotropy, Math.max(1, maxAnisotropy));
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    // The bottle is carried at the edge of a wide-angle frame and swings across
    // it; clamping keeps the keyed silhouette from wrapping onto its own far
    // side when the sampler reaches past the last texel.
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;

    const image = texture.image as { width: number; height: number };
    this.imageAspect = image.width / image.height;
    this.texture = texture;
    this.material.map = texture;
    this.material.needsUpdate = true;
  }
}
